import { TLocationState } from 'components_new/atoms/LinkWrapper'
import * as H from 'history'
import { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Context } from 'types/GlobalUserEvents'

type TQueryParams = {
  [key: string]: string[]
}

export const getQueryObject = (): TQueryParams => {
  const query = window.location.search.substring(1)
  const object: TQueryParams = {}

  if (query) {
    const vars = query.split('&')

    for (let i = 0; i < vars.length; i++) {
      const pair = vars[i].split('=')

      object[pair[0]] = pair[1].split(',')
    }
  }

  return object
}

export const createQueryString = (object: TQueryParams): string => {
  const string = Object.entries(object)
    // encodeURIComponent ensures that keys and values are URL-safe:
    .map(
      ([key, values]) =>
        `${encodeURIComponent(key)}=${values.map(encodeURIComponent).join(',')}`
    )
    .join('&')

  return `?${string}`
}

export const updateQueryParam = (params: {
  history: H.History<unknown>
  key: string
  unique?: boolean
  value: string
}) => {
  const { history, key, unique, value } = params

  const query = getQueryObject()

  const currentValues = query[key] ?? []

  const updateHistory = (search: string) => {
    const prevLocationState = {
      prevPathname:
        (history.location.state as TLocationState)?.prevPathname ?? '/',
      prevSearch: (history.location.state as TLocationState)?.prevSearch ?? ''
    }

    history.replace(
      {
        search: search
      },
      prevLocationState
    )
  }

  // remove if exists
  if (currentValues.includes(value)) {
    const newValues = currentValues.filter((item) => item !== value)

    if (newValues.length > 0) {
      // remove if more values
      updateHistory(
        createQueryString({
          ...query,
          [key]: newValues
        })
      )
    } else {
      // remove if only value
      const newQuery = { ...query }

      delete newQuery[key]

      updateHistory(createQueryString(newQuery))
    }
  } else {
    // add if exists
    if (currentValues.length > 0 && !unique) {
      updateHistory(
        createQueryString({
          ...query,
          [key]: [...currentValues, value]
        })
      )
    } else {
      updateHistory(
        createQueryString({
          ...query,
          [key]: [value]
        })
      )
    }
  }
}

export const parseQueryParamToNumber = (
  value: string | null
): number | undefined => {
  if (value === null) return undefined

  const numberValue = Number(value)

  return isNaN(numberValue) ? undefined : numberValue
}

/**
 * Helper function to append and to lower case
 * context to given url.
 * @param url - Url to add to.
 * @param context - What context to add.
 * @returns - updated url.
 */
export const addRef = (url: string, context: Context) => {
  const contextString = `ref=${context.toLowerCase()}`
  const sep = url.includes('?') ? '&' : '?'

  return url + sep + contextString
}

/**
 * Sync a query param with the URL.
 * If `defaultValue` is a string → single-value mode.
 * If `defaultValue` is a string[] → multi-value mode.
 *
 * Multiple values are stored as a single comma-separated value: ?tag=a,b
 */

// Overloads for strong typing
export function useQueryParamState<T extends string | null>(
  key: string,
  defaultValue: T,
  validValues?: readonly T[]
): [T, (newValue: T | null) => void]

export function useQueryParamState<T extends string>(
  key: string,
  defaultValue: T[],
  validValues?: readonly T[]
): [T[], (newValues: T[]) => void]

export function useQueryParamState<T extends string>(
  key: string,
  defaultValue: T | T[],
  validValues?: readonly T[]
) {
  const location = useLocation()
  const history = useHistory()
  const isMulti = Array.isArray(defaultValue)

  const filterValid = useCallback(
    (vals: T[]): T[] => {
      if (!validValues) return vals

      return vals.filter((v) => validValues.includes(v))
    },
    [validValues]
  )

  const value = useMemo<T | T[]>(() => {
    const params = new URLSearchParams(location.search)
    const raw = params.get(key) // decoded string or null

    if (isMulti) {
      if (!raw) return defaultValue

      const parts = raw
        .split(',')
        .map((s) => s.trim())
        .filter((s): s is T => Boolean(s))

      const filtered = filterValid(parts)

      return filtered.length ? filtered : defaultValue
    }

    // single
    // If multiple values exist in the URL (comma-separated), use the first.
    const candidate = (raw?.split(',')[0] ?? defaultValue) as T

    if (!validValues) return candidate

    return validValues.includes(candidate) ? candidate : defaultValue
  }, [location.search, key, isMulti, defaultValue, validValues, filterValid])

  const setParam = useCallback(
    (next: T | T[] | null) => {
      const params = new URLSearchParams(location.search)

      if (next == null) {
        params.delete(key)
      } else if (Array.isArray(next)) {
        params.set(key, next.join(','))
      } else {
        params.set(key, next)
      }

      const qs = params.toString()

      history.replace({ search: qs ? `?${qs}` : '' })
    },
    [history, key, location.search]
  )

  return [value, setParam]
}
